import React, { useState, useEffect } from 'react';
import { FaGift, FaDollarSign, FaFilter, FaSearch, FaTags, FaStar, FaShoppingCart, FaCheckCircle, FaExclamationTriangle, FaMoneyBillWave, FaUser, FaSpinner } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import { toast } from 'react-toastify';

const RewardsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 1rem;
  font-family: 'Inter', sans-serif;
  overflow-y: hidden;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem; // Reduced from 3rem
  
  h1 {
    color: #2c3e50;
    font-size: 1.8rem; // Reduced from 2.5rem
    margin-bottom: 0.5rem; // Reduced from 1rem
    font-weight: 700;
    font-family: 'Inter', sans-serif;
  }
  
  p {
    color: #7f8c8d;
    font-size: 0.95rem; // Reduced from 1.1rem
    max-width: 500px; // Reduced from 600px
    margin: 0 auto 1rem auto; // Reduced from 1.5rem
    font-family: 'Inter', sans-serif;
    line-height: 1.6;
  }
`;

const PointsSection = styled.div`
  display: grid;
  grid-template-columns: 1.3fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const RewardsGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); // Reduced from 320px
  gap: 1.2rem; // Reduced from 2rem
  margin-top: 1.5rem; // Reduced from 2rem
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem; // Reduced from 1.5rem
  }
`;

const PointsCard = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  overflow: hidden;
  transition: all 0.2s ease;
  min-height: auto;
  
  &:hover {
    box-shadow: 0 6px 16px rgba(22, 163, 74, 0.15);
    transform: translateY(-1px);
  }
`;

const CardHeader = styled.div`
  background: linear-gradient(135deg, #15803d 0%, #22c55e 100%);
  color: white;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  h3 {
    margin: 0;
    font-size: 0.95rem;
    font-family: 'Inter', sans-serif;
    font-weight: 600;
  }
  
  svg {
    font-size: 1rem;
  }
`;

const CardContent = styled.div`
  padding: 0.9rem;
`;

const PointsDisplay = styled.div`
  text-align: center;
  margin-bottom: 1.2rem; // Reduced from 1.5rem
  
  .points-value {
    font-size: 1.6rem;
    font-weight: 700;
    color: #15803d;
    margin-bottom: 0.3rem;
    font-family: 'Inter', sans-serif;
  }
  
  .points-label {
    font-size: 0.75rem;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
    font-family: 'Inter', sans-serif;
  }
`;

const CurrencyGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.8rem; // Reduced from 1rem
`;

const CurrencyItem = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.6rem;
  text-align: center;
  
  .currency-symbol {
    font-size: 1.1rem;
    margin-bottom: 0.3rem;
  }
  
  .currency-value {
    font-size: 1rem;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 0.2rem;
    font-family: 'Inter', sans-serif;
  }
  
  .currency-label {
    font-size: 0.65rem;
    color: #6b7280;
    font-weight: 500;
    font-family: 'Inter', sans-serif;
  }
`;

const ConversionNote = styled.div`
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
  padding: 0.6rem;
  margin-top: 0.9rem;
  text-align: center;
  
  .note-text {
    font-size: 0.75rem;
    color: #166534;
    font-weight: 500;
    font-family: 'Inter', sans-serif;
    margin-bottom: 0.15rem;
  }
  
  .rate-text {
    font-size: 0.65rem;
    color: #6b7280;
    font-family: 'Inter', sans-serif;
  }
`;

const RewardsSection = styled.div`
  margin-top: 2rem; // Reduced from 3rem
`;

const SectionTitle = styled.h2`
  color: #2c3e50;
  font-size: 1.5rem; // Reduced from 1.8rem
  margin-bottom: 1.2rem; // Reduced from 1.5rem
  text-align: center;
`;

const RewardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.9rem;
  margin-top: 0.8rem;
  
  @media (max-width: 968px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const RewardCard = styled.div`
  background: white;
  border-radius: 10px; // Reduced from 12px
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08); // Reduced shadow
  border: 1px solid #e2e8f0;
  overflow: hidden;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 6px 16px rgba(22, 163, 74, 0.15); // Match Dashboard hover
    transform: translateY(-1px); // Reduced from -2px
  }
`;

const RewardImage = styled.div`
  height: 80px;
  background: linear-gradient(135deg, #15803d 0%, #22c55e 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
`;

const RewardInfo = styled.div`
  padding: 0.9rem;
`;

const RewardName = styled.h3`
  color: #2c3e50;
  font-size: 0.95rem;
  margin-bottom: 0.3rem;
  font-weight: 600;
`;

const RewardDescription = styled.p`
  color: #7f8c8d;
  font-size: 0.8rem;
  margin-bottom: 0.6rem;
  line-height: 1.4;
`;

const RewardMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.6rem;
`;

const RewardPoints = styled.div`
  color: #15803d;
  font-weight: 600;
  font-size: 0.9rem;
`;

const RewardBrand = styled.div`
  color: #6b7280;
  font-size: 0.7rem;
  background: #f3f4f6;
  padding: 0.15rem 0.35rem;
  border-radius: 4px;
`;

const RedeemButton = styled.button`
  width: 100%;
  background: ${props => props.disabled ? '#e5e7eb' : 'linear-gradient(135deg, #15803d 0%, #22c55e 100%)'};
  color: ${props => props.disabled ? '#9ca3af' : 'white'};
  border: none;
  padding: 0.5rem 0.7rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  font-size: 0.8rem;
  
  &:hover {
    background: ${props => props.disabled ? '#e5e7eb' : 'linear-gradient(135deg, #166534 0%, #16a34a 100%)'};
    transform: ${props => props.disabled ? 'none' : 'translateY(-1px)'};
  }
  
  svg {
    font-size: 0.75rem;
    flex-shrink: 0;
  }
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 2rem;
  color: #6b7280;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  
  svg {
    display: block;
    margin: 0 auto 1rem auto;
  }
  
  p {
    margin: 0;
    text-align: center;
  }
`;

const Rewards = () => {
  const { user, refreshUser } = useAuth();
  const { socket } = useSocket();

  const [usdRate, setUsdRate] = useState(0.012); // Default rate: 1 INR = 0.012 USD
  const [loading, setLoading] = useState(true);
  const [rewards, setRewards] = useState([]);
  const [rewardsLoading, setRewardsLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(null);
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [withdrawalLoading, setWithdrawalLoading] = useState(false);
  const FEATURED_LIMIT = 4;

  useEffect(() => {
    fetchExchangeRate();
    fetchRewards();
  }, []);

  // Socket.io listener for real-time point updates
  useEffect(() => {
    if (socket && user) {
      const handlePointsUpdate = (data) => {
        console.log('🔍 DEBUG: Rewards - Received pointsUpdated event:', data);

        // Show toast notification based on the reason
        if (data.reason === 'withdrawal_approved') {
          toast.success(`Withdrawal approved! ${Math.abs(data.change)} points deducted.`);
        } else if (data.reason === 'withdrawal_rejected') {
          toast.info(`Withdrawal rejected. ${data.change} points restored to your account.`);
        }

        // Refresh user data to get updated points
        refreshUser();
      };

      socket.on('pointsUpdated', handlePointsUpdate);

      return () => {
        socket.off('pointsUpdated', handlePointsUpdate);
      };
    }
  }, [socket, user, refreshUser]);

  const fetchExchangeRate = async () => {
    try {
      // Using a free API for real-time exchange rates
      const response = await fetch('https://api.exchangerate-api.com/v4/latest/INR');
      const data = await response.json();
      setUsdRate(data.rates.USD || 0.012);
    } catch (error) {
      console.error('Failed to fetch exchange rate:', error);
      // Keep default rate if API fails
    } finally {
      setLoading(false);
    }
  };

  const fetchRewards = async () => {
    try {
      const token = localStorage.getItem('token');

      const response = await axios.get('/users/rewards', {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 1000, page: 1 }
      });

      if (response.data.success) {
        setRewards(response.data.rewards || []);
      } else {
        setRewards([]);
      }
    } catch (error) {
      console.error('Error fetching rewards:', error);
      setRewards([]);
    } finally {
      setRewardsLoading(false);
    }
  };

  const navigate = useNavigate();

  const handleRedeem = async (rewardId) => {
    try {
      setRedeeming(rewardId);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/users/rewards/${rewardId}/redeem`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert('Redemption requested successfully. Your voucher will be issued after admin approval.');
        await refreshUser();
        await fetchRewards();
        navigate('/payment-history');
      }
    } catch (error) {
      console.error('Redemption failed:', error);
      alert(error.response?.data?.message || 'Failed to redeem reward');
    } finally {
      setRedeeming(null);
    }
  };

  const handleWithdrawalRequest = async () => {
    try {
      setWithdrawalLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        '/users/withdrawal-request',
        { amount: parseInt(withdrawalAmount) },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert('Withdrawal request submitted successfully! Your request is being processed.');
        setWithdrawalAmount('');
        // Refresh user data to get updated points
        await refreshUser();
      }
    } catch (error) {
      console.error('Withdrawal request failed:', error);
      alert(error.response?.data?.message || 'Failed to submit withdrawal request');
    } finally {
      setWithdrawalLoading(false);
    }
  };

  const totalPoints = user?.points || 0;
  const inrValue = totalPoints; // 1 coin = ₹1
  const usdValue = totalPoints * usdRate;

  if (!user) {
    return (
      <RewardsContainer>
        <PageHeader>
          <h1>Rewards Dashboard</h1>
          <p>Please log in to view your reward points and currency conversion</p>
        </PageHeader>
      </RewardsContainer>
    );
  }

  return (
    <RewardsContainer>
      <PageHeader>
        <h1>Rewards Dashboard</h1>
        <p>View your total reward points with real-time currency conversion</p>
      </PageHeader>

      <PointsSection>
        <PointsCard>
          <CardHeader>
            <FaGift />
            <h3>Total Reward Points</h3>
          </CardHeader>
          <CardContent>
            <PointsDisplay>
              <div className="points-value">{totalPoints.toLocaleString()}</div>
              <div className="points-label">Available Points</div>
            </PointsDisplay>

            <CurrencyGrid>
              <CurrencyItem>
                <div className="currency-symbol">₹</div>
                <div className="currency-value">{inrValue.toLocaleString()}</div>
                <div className="currency-label">Indian Rupees</div>
              </CurrencyItem>

              <CurrencyItem>
                <div className="currency-symbol">$</div>
                <div className="currency-value">{loading ? '...' : usdValue.toFixed(2)}</div>
                <div className="currency-label">US Dollars</div>
              </CurrencyItem>
            </CurrencyGrid>

            <ConversionNote>
              <div className="note-text">Conversion Rate: 1 Coin = ₹1</div>
              <div className="rate-text">
                {loading ? 'Loading USD rate...' : `Current USD rate: $${usdRate.toFixed(4)} per ₹1`}
              </div>
            </ConversionNote>
          </CardContent>
        </PointsCard>

        <PointsCard>
          <CardHeader>
            <FaMoneyBillWave />
            <h3>Redeem Points</h3>
          </CardHeader>
          <CardContent>
            {!user.accountHolderName || !user.accountNumber || !user.ifscCode ? (
              <div style={{ textAlign: 'center', padding: '1rem' }}>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem', fontFamily: 'Inter, sans-serif' }}>
                  Please add your bank details to enable withdrawals
                </div>
                <button
                  onClick={() => navigate('/profile')}
                  style={{
                    background: '#22c55e',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontFamily: 'Inter, sans-serif',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    margin: '0 auto'
                  }}
                  onMouseOver={(e) => e.target.style.background = '#16a34a'}
                  onMouseOut={(e) => e.target.style.background = '#22c55e'}
                >
                  <FaUser />
                  Go to Profile
                </button>
              </div>
            ) : (
              <div>
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem', fontFamily: 'Inter, sans-serif' }}>Available Points</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#15803d', fontFamily: 'Inter, sans-serif' }}>{totalPoints.toLocaleString()} points</div>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ fontSize: '0.875rem', color: '#374151', marginBottom: '0.5rem', display: 'block', fontFamily: 'Inter, sans-serif' }}>Withdrawal Amount (Points)</label>
                  <input
                    type="number"
                    value={withdrawalAmount}
                    onChange={(e) => setWithdrawalAmount(e.target.value)}
                    min="100"
                    max={totalPoints}
                    placeholder="Enter amount to withdraw"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontFamily: 'Inter, sans-serif',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#22c55e'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  />
                </div>

                <button
                  onClick={handleWithdrawalRequest}
                  disabled={withdrawalLoading || !withdrawalAmount || withdrawalAmount < 100 || withdrawalAmount > totalPoints}
                  style={{
                    width: '100%',
                    background: withdrawalLoading || !withdrawalAmount || withdrawalAmount < 100 || withdrawalAmount > totalPoints ? '#9ca3af' : '#059669',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: withdrawalLoading || !withdrawalAmount || withdrawalAmount < 100 || withdrawalAmount > totalPoints ? 'not-allowed' : 'pointer',
                    fontFamily: 'Inter, sans-serif',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {withdrawalLoading ? (
                    <>
                      <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                      Processing...
                    </>
                  ) : (
                    <>
                      <FaMoneyBillWave />
                      Submit Withdrawal Request
                    </>
                  )}
                </button>

                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem', textAlign: 'center', fontFamily: 'Inter, sans-serif' }}>
                  Minimum withdrawal: 100 points
                </div>
              </div>
            )}
          </CardContent>
        </PointsCard>
      </PointsSection>

      <RewardsSection>
        <SectionTitle>Available Rewards</SectionTitle>

        {rewardsLoading ? (
          <LoadingSpinner>
            <FaGift style={{ fontSize: '2rem', marginBottom: '1rem' }} />
            <p>Loading available rewards...</p>
          </LoadingSpinner>
        ) : (!rewards || rewards.length === 0) ? (
          <LoadingSpinner>
            <FaExclamationTriangle style={{ fontSize: '2rem', marginBottom: '1rem' }} />
            <p>No rewards available at the moment.</p>
          </LoadingSpinner>
        ) : (
          <RewardsGrid>
            {rewards.slice(0, FEATURED_LIMIT).map((reward) => {
              const canAfford = totalPoints >= reward.pointsCost;
              const isRedeeming = redeeming === reward.id;

              return (
                <RewardCard key={reward.id}>
                  <RewardImage>
                    <FaGift />
                  </RewardImage>
                  <RewardInfo>
                    <RewardName>{reward.name}</RewardName>
                    <RewardDescription>{reward.description}</RewardDescription>

                    <RewardMeta>
                      <RewardPoints>{reward.pointsCost} points</RewardPoints>
                      {reward.brand && <RewardBrand>{reward.brand}</RewardBrand>}
                    </RewardMeta>

                    <RedeemButton
                      disabled={!canAfford || isRedeeming}
                      onClick={() => handleRedeem(reward.id)}
                    >
                      {isRedeeming ? (
                        <>
                          <FaShoppingCart />
                          Redeeming...
                        </>
                      ) : !canAfford ? (
                        <>
                          <FaExclamationTriangle />
                          Insufficient Points
                        </>
                      ) : (
                        <>
                          <FaShoppingCart />
                          Redeem Now
                        </>
                      )}
                    </RedeemButton>
                  </RewardInfo>
                </RewardCard>
              );
            })}
          </RewardsGrid>
        )}
      </RewardsSection>
    </RewardsContainer>
  );
};

export default Rewards;
